import { invariant } from '../util/invariant';
import { isValidGUID } from '../util/general';
import { isObject } from '@mimir/primitive/type-guards/isObject';
import type { TransferrableAttachment } from './attachmentPrimitives';

export enum SidecarKind {
  'EVS-1.0',
  'Mimir JSON',
  'None'
}

export type SidecarKindType = keyof typeof SidecarKind;
const validSidecarKinds: ReadonlySet<SidecarKindType> = new Set(['EVS-1.0', 'Mimir JSON']);

export const isValidSidecarKind = (kind: SidecarKindType) => {
  return validSidecarKinds.has(kind);
};

export interface StorageType {
  id: string;
  name: string;
  private: boolean;
  owner: string;
  sidecarXmlKind?: SidecarKindType;
  description?: string;
  keldaId?: string;
  attachmentToTransfer?: TransferrableAttachment;

  transferAttachments?: boolean;
  attachmentTransferRules?: AttachmentRule[];

  visibleTo?: string[]; // only available for kelda storages
}

export const isStorageType = (obj: unknown): obj is StorageType => {
  if (typeof obj !== 'object') {
    return false;
  }
  if (!obj) {
    return false;
  }

  const possibleStorageType = obj as StorageType;
  const transferAttachmentsPresent = 'transferAttachments' in possibleStorageType ? typeof possibleStorageType.transferAttachments === 'boolean' : true;
  let rulesPresent = false;

  if ('attachmentTransferRules' in possibleStorageType) {
    if (Array.isArray(possibleStorageType.attachmentTransferRules)) {
      rulesPresent = possibleStorageType.attachmentTransferRules.every(isInstanceofAttachmentRule);
    } else {
      rulesPresent = false;
    }
  } else {
    rulesPresent = true;
  }

  return 'id' in obj && 'name' in obj && transferAttachmentsPresent && rulesPresent;
};

export const newStorage = (): StorageType => {
  return {
    id: '',
    name: '',
    private: true,
    owner: '',
    description: '',
  };
};

export interface Storages {
  default: StorageType | undefined;
  all: StorageType[];
}

export enum TenantStorageStatus {
  Default = 'default',
  Active = 'active'
}

export enum TenantStorageType {
  Archive = 'archive',
  Export = 'export',
}

/**
 * Generates a unique ID for a Kelda storage by combining the kelda configuration ID and the
 * name of the storage folder. Should only be used to generate a new storage folder configuration
 * or as a fall back when to explicit ID is defined for the storage.
 */
export const getDefaultKeldaStorageId = (keldaConfigId: string, storageFolderName: string) => {
  invariant(isValidGUID(keldaConfigId), 'keldaConfigId is assumed to be guid');
  invariant(storageFolderName, 'storageFolderName is assumed to be non empty');
  return `${keldaConfigId}_${storageFolderName}`;
};


export interface AttachmentRule {
  fileNameTemplate?: string;
  filter: {
    language?: string;
    extension?: string;
  }
}

const isInstanceofAttachmentRule = (obj: unknown): obj is AttachmentRule => {
  if (isObject(obj) && 'filter' in obj) {
    return true;
  }
  return false;
};
